<docs>
  "See More Products" is displayed if there are greater than 26 buy-products or
  reward-products. The backend will return a maximum of 30 products due to some
  coupons having over 26,000 SKU's associated resulting in a performance hit.
</docs>

<template>
  <div>
    <div
      class="coupon-detail-modal coupon-detail-modal__loading"
      v-if="!isLoaded"
    >
      Loading Coupon...
    </div>
    <div class="coupon-detail-modal coupon-detail-modal__loading" v-if="errors">
      <div class="mb-3">Oops! This coupon is no longer available.</div>
      <div class="mb-3">
        Don’t miss 100+ in available savings.
        <a href="/digital-coupon/coupon-selection" class="system-link">
          Browse Coupons <span class="fa fa-arrow-right"></span>
        </a>
      </div>
    </div>
    <div
      class="coupon-detail-modal"
      id="CDM-Container"
      v-if="isLoaded && !errors"
    >
      <section class="row mb-3 align-items-center justify-content-center">
        <div
          class="col-4 text-center mr-4 coupon-detail-modal__image-container"
        >
          <img v-bind:src="coupon.imageUrl" class="coupon-detail-modal__image" />
          <div
            v-if="isInStoreOnlyCoupon"
            class="promo-flag--in-store-only--short"
          >
            <div class="coupon-flag-text text-left">In Store Only</div>
          </div>
        </div>
        <div class="coupon-detail-modal__description col-7">
          <div
            class="coupon-detail-modal__bubblecallout"
            v-html="coupon.bubbleCallOut"
          ></div>
          <div
            class="coupon-detail-modal__display-name"
            v-html="coupon.displayName"
          ></div>
          <div
            class="coupon-detail-modal__expiration-date text-muted mb-2 mt-2"
          >
            <div
              class="coupon-detail-modal__days-left"
              v-if="coupon.daysLeft <= 3"
            >
              <div class="coupon-detail-modal__days-left-number">
                {{ actualDaysLeft }}
              </div>
              {{ actualDaysLeft | pluralize("day") }} left
            </div>
            Expires: {{ coupon.expirationDate }}, {{ coupon.rewardLimit }}
          </div>
          <div class="d-none d-md-block mx-auto">
            <div v-show="coupon.unclipped">
              <button
                class="btn btn-coupon mb-3"
                @click="clipCoupon(coupon.couponId);"
              >
                <i class="fa fa-scissors" aria-hidden="true"></i> Clip
              </button>
            </div>

            <div v-show="!coupon.unclipped">
              <button
                class="btn btn-coupon btn-coupon-clipped btn-disabled mb-3"
              >
                <i class="fa fa-scissors" aria-hidden="true"></i> Clipped
              </button>
            </div>

            <div v-if="orderContext === 'curbside'">
              <closeness-qualifier
                v-bind:cq="cq"
                v-bind:coupon="coupon"
                v-for="(cq, index) in coupon.closenessQualifiers"
                v-bind:key="index"
                v-if="coupon"
                class=""
              >
              </closeness-qualifier>
            </div>
            <social-sharing v-bind:coupon="coupon" location="CDM">
            </social-sharing>
          </div>
        </div>
        <div class="d-md-none p-3 mw-100 w-100">
          <div v-show="coupon.unclipped">
            <button
              class="btn btn-coupon btn-full-width mb-3"
              @click="clipCoupon(coupon.couponId);"
            >
              <i class="fa fa-scissors" aria-hidden="true"></i> Clip
            </button>
          </div>

          <div v-show="!coupon.unclipped">
            <button
              class="btn btn-coupon btn-coupon-clipped btn-full-width btn-disabled mb-3"
            >
              <i class="fa fa-scissors" aria-hidden="true"></i> Clipped
            </button>
          </div>

          <div v-if="orderContext === 'curbside'">
            <closeness-qualifier
              v-bind:cq="cq"
              v-bind:coupon="coupon"
              v-for="(cq, index) in coupon.closenessQualifiers"
              v-bind:key="index"
              v-if="coupon"
            >
            </closeness-qualifier>
          </div>
          <social-sharing v-bind:coupon="coupon" location="CDM">
          </social-sharing>
        </div>
      </section>

      <section>
        <div class="coupon-detail-modal__list-title my-2">
          <div class="coupon-detail-modal__products-in-offer">
            <template
              v-if="!coupon.rewardItems.length && coupon.buyItems.length"
            >
              Products in this offer
            </template>
            <template
              v-if="
                coupon.rewardItems.length &&
                  coupon.buyItems.length &&
                  !coupon.buyDollarAmount &&
                  !coupon.buyQuantityUnits
              "
            >
              Buy 1 of these items
            </template>
            <template
              v-if="coupon.rewardItems.length && coupon.buyDollarAmount"
            >
              Buy {{ coupon.buyDollarAmount }} of these items
            </template>
            <template
              v-if="coupon.rewardItems.length && coupon.buyQuantityUnits"
            >
              Buy {{ coupon.buyQuantityUnits }} of these items
            </template>
          </div>

          <!--
            <c:when test="${empty coupon.rewardItems}">
            Products in this offer
            </c:when>
            <c:when test="${not empty coupon.rewardItems && not empty coupon.buyDollarAmount}">
            Buy ${coupon.buyDollarAmount} of these items
            </c:when>
            <c:when test="${not empty coupon.rewardItems && not empty coupon.buyQuantityUnits}">
            Buy ${coupon.buyQuantityUnits} of these items
            </c:when>
            <c:otherwise>
            Buy these items
            </c:otherwise>
          -->

          <div v-if="coupon.couponDisplayType !== 'T' && !isInStoreOnlyCoupon">
            <a
              v-bind:href="'/search/coupon-detail/?q=' + couponId"
              class="system-link font-weight-bold"
              v-if="howManyMoreProducts"
            >
              See More {{ isMobile ? "" : "Products" }}
            </a>
          </div>
        </div>

        <ul class="products">
          <product-card
            :product="product.product"
            :hideAddToCartBtn="isInStoreOnlyCoupon"
            listname="Products in this offer"
            size="small"
            location="CDM"
            v-bind:key="index"
            v-for="(product, index) in coupon.buyItems"
            v-if="index < 26"
            class="mb-3 flex-grow-1"
            :position="index + 1"
          >
          </product-card>
        </ul>
        <div class="float-right my-1">
          <div v-if="coupon.couponDisplayType !== 'T' && !isInStoreOnlyCoupon">
            <a
              v-bind:href="'/search/coupon-detail/?q=' + couponId"
              class="system-link font-weight-bold"
              v-if="howManyMoreProducts"
            >
              See More {{ isMobile ? "" : "Products" }}
            </a>
          </div>
        </div>
      </section>
      <section v-if="coupon.rewardItems.length">
        <div class="coupon-detail-modal__list-title my-2">
          <div class="coupon-detail-modal__products-in-offer">
            Get these items to receive discount
          </div>

          <div>
            <a
              v-bind:href="'/search/coupon-detail/?q=' + couponId"
              class="system-link-fat"
              v-if="
                coupon.couponDisplayType != 'T' &&
                  coupon.rewardItems > 20 &&
                  !isInStoreOnlyCoupon
              "
            >
              See More {{ isMobile ? "" : "Products" }}
            </a>
          </div>
        </div>

        <ul class="products">
          <product-card
            :product="product.product"
            :hideAddToCartBtn="isInStoreOnlyCoupon"
            listname="Get these items to receive discount"
            size="small"
            location="CDM"
            v-bind:key="index"
            v-for="(product, index) in coupon.rewardItems"
            v-if="index < 20"
            class="mb-3 flex-grow-1"
            :position="index + 1"
          >
          </product-card>
        </ul>

        <div class="text-center my-1">
          <a
            v-bind:href="'/search/coupon-detail/?q=' + couponId"
            class="system-link-fat"
            v-if="
              coupon.couponDisplayType != 'T' &&
                coupon.rewardItems > 20 &&
                !isInStoreOnlyCoupon
            "
          >
            See More {{ isMobile ? "" : "Products" }}
          </a>
        </div>
      </section>

      <div class="coupon-detail-modal__terms">
        Terms and Conditions: {{ coupon.termsAndConditions }}
      </div>
    </div>
  </div>
</template>

<script>
import ProductCard from "./product/ProductCard";
import ClosenessQualifier from "./ClosenessQualifier";
import SocialSharing from "./sharing/SocialSharing";

export default {
  name: "CouponDetailModal",
  props: {
    couponId: {
      //   type: Number or String :(
      required: true
    },
    position: {
      type: Number,
      default: 1
    }
  },
  components: {
    "product-card": ProductCard,
    "closeness-qualifier": ClosenessQualifier,
    "social-sharing": SocialSharing
  },
  data() {
    return {
      orderContext: window.heb.data.user.orderContext,
      isLoaded: false,
      weight: 0,
      coupon: {}
    };
  },
  computed: {
    howManyMoreProducts() {
      if (this.coupon.buyItems.length > 26) {
        return this.coupon.buyItems.length;
      }
    },
    totalCommerceItemCount() {
      return this.$store.state.cart.totalCommerceItemCount;
    },
    actualDaysLeft() {
      if (this.coupon.daysLeft === 0) {
        return 1;
      } else {
        return this.coupon.daysLeft;
      }
    },
    isMobile() {
      return window.heb.screen.isSmall;
    },
    isInStoreOnlyCoupon() {
      return this.coupon.inStoreOnly;
    }
  },
  methods: {
    clipCoupon() {
      if (!window.heb.data.user.isGuest) {
        var vm = this;
        this.gtmClickCoupon("e_selectCoupon", this.coupon, this.position);
        this.$store.dispatch("cart/CLIP_COUPON", {
          couponId: this.couponId,
          callback: vm.getCouponDetails
        });
      } else {
        window.location = "/my-account/login";
      }
    },
    getCouponDetails: function() {
      var vm = this;
      VueStore.$store.dispatch("cart/FETCH_COUPON_DETAILS", {
        id: this.couponId,
        callback: vm.processCouponResponse,
        summary: false
      });
    },
    processCouponResponse(response) {
      this.isLoaded = true;

      if (response.errors) {
        this.errors = true;
        return;
      }

      this.coupon = response;
    }
  },
  mounted: function() {
    this.getCouponDetails();
  },
  watch: {
    totalCommerceItemCount(newCount, oldCount) {
      // # of items changed, better refresh percent complete
      this.getCouponDetails();
    }
  }
};
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../assets/lib";
@import "../../../static-assets/css/_btn.scss";

.coupon-detail-modal {
  max-width: 580px;
  margin: 0 auto;

  &__loading {
    text-align: center;
  }

  &__bubblecallout {
    font-weight: bold;
    font-size: 20px;
    color: red;
  }

  &__description {
    text-align: left;
  }

  &__display-name {
    font-size: 16px;
    font-weight: bold;
  }

  &__days-left {
    float: left;
    margin-right: 6px;
    text-transform: uppercase;
    color: #dc291e;
  }

  &__days-left-number {
    display: inline-flex;
    background-color: $color-divider-light-gray;
    border-radius: 50%;
    font-weight: 600;
    width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    color: #dc291e;
  }

  &__terms {
    margin-top: 2rem;
    font-size: 12px;
    line-height: 15px;
    text-align: center;
    color: #8b8b8b;
  }

  &__list-title {
    display: flex;
    justify-content: space-between;
  }

  &__products-in-offer {
    font-size: 14px;
    font-weight: bold;
  }

  &__expiration-date {
    font-size: 11px;
  }

  &__image-container {
    min-height: 155px;
  }

  &__image {
    max-width: 100%;
  }
}

.products {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: -4px;
}

.products li {
  height: 100%;
}

.products .product-card--small {
  border-bottom: 1px solid $color-divider-light-gray;
}

@media screen and (min-width: $breakpoint-tablet) {
  .coupon-detail-modal {
    &__description {
      text-align: center;
    }

    &__display-name {
      font-size: 20px;
    }

    &__expiration-date {
      font-size: 14px;
    }

    &__products-in-offer {
      font-size: 16px;
    }

    &__image-container {
      min-height: auto;
    }
  }

  .products {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 10px;
    grid-column-gap: 5%;
  }
  .products li {
    width: 100%;
    height: auto !important;
  }

  .products .product-card--small {
    border: 1px solid $color-divider-light-gray;
    border-radius: 5px;
  }
}
</style>
